// src/views/pages/Positionreport.js
//
// New UI (cream/gold theme — Image 2) with ALL old UI functionality:
//   ✅ Checkboxes (select rows for bulk close / rollover)
//   ✅ Close ✕ button per row → ClosePositionModal (Lot/Qty/Price + Market/Limit)
//   ✅ Rollover button (bottom-left of table, same as old UI)
//   ✅ Close Position bulk button (header area, disabled when nothing selected)
//   ✅ Confirm-all modal before bulk close
//   ✅ Infinite scroll (same logic as TablePositionReport)
//   ✅ Live socket LTP / MTM updates
//   ✅ Sort by column click
//   ✅ Debounced search
//   ✅ Auto-reload after close/rollover (isClosePosition watcher)
//   ✅ All filters: Day/Net, Outstanding/Closed, Segment, Valan, Report Type,
//      Client, Symbol, Authority, Ex Date

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import { selectFieldStyles } from 'Store/styled/select';
import moment from 'moment';
import _ from 'lodash';
import "../../assets/scss/position-report-embedded.scss";
import { FONT_ROBOTO_MONO } from "../../utils/fonts";
import { api, setAuthToken } from '../../utils/api';
import {
  confirmRolloverPositions,
  alertSelectPositionFirst,
} from '../../utils/tradingAlerts';
import { getValansList } from '../../Store/Reducer/valan.slice';
import useAppLanguage from "hooks/useAppLanguage";
import { tSide } from "utils/appLanguage";

import {
  getPositionReportList,
  exportPositionReport,
  closePosition,
  saveAllClosePosition,
  rollOverPosition,
  setPositionReportSearchText,
  setStartPagePositionReport,
  setPositionReportFilterValue,
  setPositionReportList,
  setfilteredPositionReportList,
  setClosePositonSelected,
  setPositionCloseConfirmDialog,
  setPositionCloseData,
  setIsClosePosition,
  setPositionDisplayNames,
  setSocketData,
} from '../../Store/Reducer/Positionreport.slice';
import { setIsAddedOrder } from '../../Store/Reducer/marketwatch.slice';
import { deleteOrder, getOrderBookList } from '../../Store/Reducer/orderbook.slice';
import getSocket from '../../utils/socketInstance';
import { formatOpenPositionTime, applyLiveQuoteToOpenPosition } from '../../utils/openPositionMapper';
import { applyLivePnlToMarginSummary, sumLivePositionPnl } from '../../utils/marginSummaryLive';
import { useBrokerTimeZoneId } from '../../utils/dateFormats';
import { resolveSegmentLabel } from '../../utils/segmentUtils';
import AccountMarginBar from '../../components/account/AccountMarginBar';
import { getMarginSummary } from '../../Store/Reducer/margin.slice';
import useMobileViewport from "hooks/useMobileViewport";
import Swal from "sweetalert2";

// ── Session ───────────────────────────────────────────────────────────────────
function getSessionData() {
  try {
    const raw = sessionStorage.getItem('fxbazzar_user')
             || sessionStorage.getItem('fxbazzar_admin')
             || '{}';
    const d = JSON.parse(raw);
    return {
      userId  : d.userId   || d.id       || d.UserId   || '',
      userName: d.userName || d.UserName || d.name     || '',
      userRole: d.role     || d.Role     || (d.roles && d.roles[0]) || 'User',
      roles   : d.roles    || d.Roles    || [d.role || d.Role || 'User'],
      token   : d.token    || d.Token    || '',
    };
  } catch {
    return { userId:'', userName:'', userRole:'User', roles:['User'], token:'' };
  }
}

/** Live LTP/bid/ask for a pending order symbol from market watch. */
function resolvePendingCurrentPrice(row, marketWatchList) {
  if (!row || !marketWatchList) return 0;
  const keys = [
    row.displayName,
    row.DisplayName,
    row.script,
    row.Script,
    row.symbol,
    row.Symbol,
  ]
    .filter(Boolean)
    .map(String);
  const tryRow = (q) => {
    if (!q || typeof q !== "object") return 0;
    const side = String(row.orderTypeName || row.OrderTypeName || "").toLowerCase();
    const exec = String(row.executionTypeName || row.ExecutionTypeName || "").toLowerCase();
    const isBuy = side.includes("buy") || exec.includes("buy");
    const ltp = Number(q.lastTradedPrice ?? q.ltp ?? 0);
    const bid = Number(q.buyPrice ?? q.bid ?? 0);
    const ask = Number(q.sellPrice ?? q.ask ?? 0);
    if (isBuy && ask > 0) return ask;
    if (!isBuy && bid > 0) return bid;
    if (ltp > 0) return ltp;
    return ask || bid || 0;
  };
  if (marketWatchList instanceof Map) {
    for (const k of keys) {
      if (marketWatchList.has(k)) {
        const n = tryRow(marketWatchList.get(k));
        if (n > 0) return n;
      }
    }
    if (marketWatchList.size <= 400) {
      for (const v of marketWatchList.values()) {
        const hit =
          keys.includes(String(v?.displayName || "")) ||
          keys.includes(String(v?.script || "")) ||
          keys.includes(String(v?.symbol || ""));
        if (hit) {
          const n = tryRow(v);
          if (n > 0) return n;
        }
      }
    }
  }
  return 0;
}

function pendingOrderDigits(rate) {
  const s = String(rate ?? "");
  const i = s.indexOf(".");
  if (i < 0) return 2;
  return Math.min(5, Math.max(2, s.length - i - 1));
}

// ── Design tokens — light (cream/teal) ───────────────────────────────────────
const C_LIGHT = {
  headerBg    : '#1A7A70',
  headerBorder: '#145e57',
  pageBg      : '#f4fbfa',
  panelBg     : '#ffffff',
  panel2Bg    : '#edf7f6',
  border      : '#cfe5e3',
  tx          : '#0f3e3a',
  muted       : '#6b8a87',
  green       : '#1A7A70',
  red         : '#ef5350',
  accent      : '#2f9e93',
  checkBg     : 'rgba(26,122,112,0.08)',
};

const C_DARK = {
  headerBg    : '#1A7A70',
  headerBorder: '#145e57',
  pageBg      : 'var(--bg-primary, #131722)',
  panelBg     : 'var(--bg-panel, #131722)',
  panel2Bg    : 'var(--bg-secondary, #1a1f2c)',
  border      : 'var(--border, rgba(255,255,255,0.09))',
  tx          : 'var(--text-primary, #ffffff)',
  muted       : 'var(--text-muted, rgba(255,255,255,0.48))',
  green       : 'var(--green, #26a69a)',
  red         : '#ef5350',
  accent      : '#26a69a',
  checkBg     : 'rgba(38, 166, 154, 0.12)',
};

/** Chart-docked panel: align with Fxbazzar / MarketWatch (beige shell + teal accent) */
const EMB = {
  card: "var(--bg-primary, #f4f0ec)",
  card2: "var(--bg-secondary, #ede8e3)",
  panel: "#fffef9",
  border: "var(--border, #c0b8ae)",
  borderSoft: "#ddd5ca",
  text: "var(--text-primary, #2a1a1a)",
  muted: "var(--text-muted, #7a7168)",
  teal: "var(--green, #1a7a70)",
  tealSoft: "rgba(26, 122, 112, 0.12)",
  btnSecondary: "#8a8276",
};

// ── react-select style factory ────────────────────────────────────────────────
function selSt(colors = C_LIGHT) {
  return {
    ...selectFieldStyles,
    control           : b => ({ ...b, background:colors.panelBg, borderColor:colors.border, minHeight:34, fontSize:13, boxShadow:'none', cursor:'pointer', borderRadius:4 }),
    singleValue       : b => ({ ...selectFieldStyles.singleValue(b), color:colors.tx }),
    input             : b => ({ ...selectFieldStyles.input(b), color:colors.tx }),
    placeholder       : b => ({ ...selectFieldStyles.placeholder(b), color:colors.muted, fontSize:13 }),
    menu              : b => ({ ...b, background:colors.panelBg, border:`1px solid ${colors.border}`, zIndex:9999 }),
    option            : (b,s) => ({ ...b, background:s.isFocused?colors.panel2Bg:colors.panelBg, color:colors.tx, fontSize:13, cursor:'pointer' }),
    clearIndicator    : b => ({ ...b, padding:'2px 4px', cursor:'pointer' }),
    dropdownIndicator : b => ({ ...b, padding:'2px 4px' }),
    indicatorSeparator: () => ({ display:'none' }),
  };
}

/** Tighter react-select for embedded panel below chart */
function selStCompact(colors = C_LIGHT) {
  return {
    ...selectFieldStyles,
    control           : b => ({ ...b, background:colors.panelBg, borderColor:colors.border, minHeight:30, fontSize:11, boxShadow:'none', cursor:'pointer', borderRadius:6 }),
    singleValue       : b => ({ ...selectFieldStyles.singleValue(b), color:colors.tx, fontSize:11 }),
    input             : b => ({ ...selectFieldStyles.input(b), color:colors.tx, fontSize:11 }),
    placeholder       : b => ({ ...selectFieldStyles.placeholder(b), color:colors.muted, fontSize:11 }),
    menu              : b => ({ ...b, background:colors.panelBg, border:`1px solid ${colors.border}`, zIndex:9999, fontSize:11 }),
    option            : (b,s) => ({ ...b, background:s.isFocused?colors.panel2Bg:colors.panelBg, color:colors.tx, fontSize:11, cursor:'pointer', padding:'6px 8px' }),
    clearIndicator    : b => ({ ...b, padding:'2px', cursor:'pointer' }),
    dropdownIndicator : b => ({ ...b, padding:'2px' }),
    indicatorSeparator: () => ({ display:'none' }),
  };
}

/** react-select for embedded dock — matches trading shell chrome */
function selStEmbeddedShell() {
  return {
    ...selectFieldStyles,
    control: (b) => ({
      ...b,
      background: EMB.panel,
      borderColor: EMB.border,
      minHeight: 30,
      fontSize: 11,
      boxShadow: "none",
      cursor: "pointer",
      borderRadius: 8,
    }),
    singleValue: (b) => ({ ...selectFieldStyles.singleValue(b), color: EMB.text, fontSize: 11 }),
    input: (b) => ({ ...selectFieldStyles.input(b), color: EMB.text, fontSize: 11 }),
    placeholder: (b) => ({ ...selectFieldStyles.placeholder(b), color: EMB.muted, fontSize: 11 }),
    menu: (b) => ({ ...b, background: EMB.panel, border: `1px solid ${EMB.border}`, zIndex: 9999, fontSize: 11 }),
    option: (b, s) => ({
      ...b,
      background: s.isFocused ? EMB.card2 : EMB.panel,
      color: EMB.text,
      fontSize: 11,
      cursor: "pointer",
      padding: "6px 8px",
    }),
    clearIndicator: (b) => ({ ...b, padding: "2px", cursor: "pointer" }),
    dropdownIndicator: (b) => ({ ...b, padding: "2px" }),
    indicatorSeparator: () => ({ display: "none" }),
  };
}

const inpSt = (colors = C_LIGHT) => ({
  background  : colors.panelBg,
  border      : `1px solid ${colors.border}`,
  color       : colors.tx,
  padding     : '6px 10px',
  borderRadius: 4,
  fontSize    : 13,
  outline     : 'none',
  width       : '100%',
  boxSizing   : 'border-box',
  fontFamily  : 'inherit',
});

/** Table header — same family as Market Watch / index strip: light bar, dark text */
const thSt = () => ({
  padding      : "9px 12px",
  fontSize     : 11,
  fontWeight   : 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color        : "var(--text-primary, #3d3630)",
  background   : "var(--bg-header, #ddd6cc)",
  whiteSpace   : "nowrap",
  position     : "sticky",
  top          : 0,
  zIndex       : 2,
  borderBottom : "1px solid var(--border, #c0b8ae)",
  borderRight  : "1px solid var(--border-light, #e8e4df)",
  cursor       : "pointer",
  userSelect   : "none",
  textAlign    : "left",
  boxShadow    : "inset 0 -1px 0 rgba(42, 26, 26, 0.06)",
});

const tdSt = (tint) => ({
  padding     : "8px 12px",
  whiteSpace  : "nowrap",
  fontSize    : 13,
  color       : "var(--text-primary, #0f3e3a)",
  background  :
    tint === "buy"
      ? "color-mix(in srgb, var(--green, #1a7a70) 8%, var(--bg-primary, #fff) 92%)"
      : tint === "sell"
        ? "color-mix(in srgb, #ef5350 7%, var(--bg-primary, #fff) 93%)"
        : "var(--bg-primary, #ffffff)",
  borderBottom: "1px solid var(--border-light, #e8e4df)",
  borderRight : "1px solid var(--border-light, #e8e4df)",
});

const btnSt = (bg, disabled) => ({
  padding     : '7px 20px',
  background  : disabled ? '#ccc' : bg,
  color       : '#fff',
  border      : 'none',
  borderRadius: 4,
  cursor      : disabled ? 'not-allowed' : 'pointer',
  fontSize    : 13,
  fontWeight  : 600,
  whiteSpace  : 'nowrap',
  opacity     : disabled ? 0.75 : 1,
});

const btnStCompact = (bg, disabled) => ({
  ...btnSt(bg, disabled),
  padding: '5px 12px',
  fontSize: 11,
  borderRadius: 6,
});

// ── Options ───────────────────────────────────────────────────────────────────
const CR_TYPES   = [{ value:'day', label:'Day Wise' }, { value:'net', label:'Net Wise' }];
const DISP_TYPES = [{ value:'outstanding', label:'Outstanding' }, { value:'all', label:'Closed' }];
const REPORT_TYPES = [
  { value:'client', label:'Client Wise' },
  { value:'script', label:'Script Wise' },
  { value:'self',   label:'Self Wise'   },
];

/** Full-viewport overlay — must portal to body (embedded position panel clips fixed children). */
function ClosePositionPortal({ children, onBackdropClick, loading, darkTheme, mobileMt5 = false }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  /* MT5: hide mobile footer while Modify Position full page is open */
  useEffect(() => {
    if (!mobileMt5 || typeof document === "undefined") return undefined;
    document.body.classList.add("fx-mt5-fullpage-open");
    return () => document.body.classList.remove("fx-mt5-fullpage-open");
  }, [mobileMt5]);

  if (typeof document === "undefined") return null;

  const themeClass =
    darkTheme || document.querySelector(".trading-platform.dark") ? "pr-close-pop--dark" : "pr-close-pop--light";

  return createPortal(
    <div
      className={`pr-close-pop ${themeClass}${mobileMt5 ? " pr-close-pop--mt5-mobile" : ""}`}
      role="presentation"
    >
      <div
        className="pr-close-pop__backdrop"
        aria-hidden
        onClick={loading ? undefined : onBackdropClick}
      />
      <div
        className="pr-close-pop__card"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

function fmtClosePx(n, dp = 2) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toFixed(dp);
}

function fmtClosePnlAbs(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0.00";
  return Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Strip trailing expiry (`28Apr2026`) so Trade rows show symbol only. */
function stripExpiryFromSymbol(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const stripped = raw.replace(/\s*\d{1,2}[A-Za-z]{3}\d{4}\s*$/i, "").trim();
  return stripped || raw;
}

function tradeSymbolLabel(row) {
  const raw = String(row?.script || row?.symbol || row?.displayName || "").trim();
  return stripExpiryFromSymbol(raw) || "—";
}

/** Expiry for Trade tap popup only — not attached to the list symbol. */
function tradeExpiryLabel(row) {
  const fromName = [row?.displayName, row?.script, row?.symbol];
  for (const v of fromName) {
    const m = String(v || "").trim().match(/(\d{1,2}[A-Za-z]{3}\d{4})\s*$/i);
    if (m) return m[1];
  }
  const expRaw =
    row?.exDate ??
    row?.ExDate ??
    row?.expiryDate ??
    row?.ExpiryDate ??
    row?.expiry ??
    row?.Expiry;
  if (expRaw == null || expRaw === "") return "";
  const s = String(expRaw).trim();
  if (/^\d{1,2}[A-Za-z]{3}\d{4}$/i.test(s)) return s;
  const parsed = moment(
    s,
    ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "YYYYMMDD", "YYYY/MM/DD", moment.ISO_8601],
    true
  );
  if (parsed.isValid()) return parsed.format("DDMMMYYYY");
  const loose = moment(s);
  return loose.isValid() ? loose.format("DDMMMYYYY") : "";
}

/** MT5 orange Close button copy: Close #id buy 0.01 SYMBOL open at ltp with Loss/Profit x */
function buildMt5CloseButtonLabel(row, lot, price) {
  const id = row.positionId ?? row.PositionId ?? row.rowId ?? "—";
  const netQty = Number(row.netQty) || 0;
  const side = netQty > 0 ? "buy" : netQty < 0 ? "sell" : "—";
  const symbol = tradeSymbolLabel(row);
  const dp = row.digitAfterPoint ?? 2;
  const openPx = fmtClosePx(row.openPrice, dp);
  const atPx = fmtClosePx(price ?? row.ltp ?? row.currentPrice, dp);
  const lotTxt = Number.isFinite(Number(lot)) ? String(lot) : "—";
  const netLot = Math.abs(Number(row.netLot) || 0) || 1;
  const fullMtm = (Number(row.profit ?? row.mtm) || 0) * (row.currencyRate || 1);
  const ratio = Math.abs(Number(lot) || 0) / netLot;
  const pnl = fullMtm * (Number.isFinite(ratio) ? ratio : 1);
  const tone = pnl > 0 ? "Profit" : pnl < 0 ? "Loss" : "Profit";
  const signed =
    pnl === 0 ? "0.00" : `${pnl < 0 ? "-" : ""}${fmtClosePnlAbs(pnl)}`;
  return `Close #${id} ${side} ${lotTxt} ${symbol} ${openPx} at ${atPx} with ${tone} ${signed}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// CLOSE POSITION MODAL (mirrors old ClosePositionModal from TablePositionReport)
// Mobile MT5: full-page Modify Position look (Lot/Qty/Price only — no SL/TP/Comment)
// ─────────────────────────────────────────────────────────────────────────────
function ClosePositionModal({ row, loading, onClose, onConfirm, darkTheme = false, mobileMt5 = false }) {
  const { t } = useAppLanguage();
  /** Inline confirm (fxbazzar_frontend used Swal here — second Swal over this modal hangs on mobile). */
  const [phase, setPhase] = useState("edit");
  const [marketType, setMarketType] = useState('market');
  const [lot,   setLot]   = useState(Math.abs(row.netLot  || 0));
  const [qty,   setQty]   = useState(Math.abs(row.netQty  || 0));
  const [price, setPrice] = useState(row.ltp || row.currentPrice || 0);
  const [errors, setErrors] = useState({});
  const oneQtySize = row.netLot ? Math.abs(row.netQty / row.netLot) : 1;
  const maxLot = Math.abs(row.netLot || 0);
  const maxQty = Math.abs(row.netQty || 0);
  const lotStep = maxLot >= 1 ? 0.01 : Math.min(0.01, maxLot || 0.01);
  const qtyStep = oneQtySize || 1;
  const priceDp = row.digitAfterPoint ?? 2;
  const priceStep = Number((1 / Math.pow(10, priceDp)).toFixed(priceDp)) || 0.01;
  const symbol = tradeSymbolLabel(row);
  const symbolTitle = symbol;
  const expiryLabel = tradeExpiryLabel(row);
  const netQty = Number(row.netQty) || 0;
  const side = netQty > 0 ? "buy" : netQty < 0 ? "sell" : "—";
  const ticket = row.positionId ?? row.PositionId ?? row.rowId ?? "—";
  const openPx = fmtClosePx(row.openPrice, priceDp);
  const summaryLine = `#${ticket} ${side} ${lot} ${symbol} ${openPx}`;
  /** Live LTP for orange Close line only. Readonly Price box stays frozen at open click. */
  const liveMarketPx = Number(row.ltp ?? row.currentPrice ?? 0) || 0;
  const closeAtPrice = marketType === "market" ? (liveMarketPx || price) : price;
  const closeBtnLabel = buildMt5CloseButtonLabel(row, lot, closeAtPrice);

  useEffect(() => {
    setPhase("edit");
    setMarketType("market");
    setLot(Math.abs(row.netLot || 0));
    setQty(Math.abs(row.netQty || 0));
    // Freeze Price textbox at the moment Modify Position opens
    setPrice(row.ltp || row.currentPrice || 0);
    setErrors({});
  }, [row.rowId]);

  const setLotLinked = (nextLot) => {
    const clamped = Math.min(maxLot, Math.max(0, Number(nextLot) || 0));
    const rounded = Number(clamped.toFixed(4));
    setLot(rounded);
    setQty(Math.round(Math.abs(rounded * oneQtySize)));
  };

  const bumpLot = (dir) => {
    setLotLinked(Number(lot) + dir * lotStep);
  };

  const bumpQty = (dir) => {
    if (row.segmentId === 2 || row.segmentId === 3) return;
    const next = Math.min(maxQty, Math.max(0, Number(qty) + dir * qtyStep));
    setQty(next);
    if (oneQtySize) setLot(Number((next / oneQtySize).toFixed(4)));
  };

  const bumpPrice = (dir) => {
    if (marketType === "market") return;
    const next = Math.max(0, Number(price) + dir * priceStep);
    setPrice(Number(next.toFixed(priceDp)));
  };

  const validate = () => {
    const e = {};
    const px = closeAtPrice;
    if (!lot  || lot  <= 0)                          e.lot   = `Lot required`;
    if (!qty  || qty  <= 0)                          e.qty   = `Qty required`;
    if (!px || px <= 0)                              e.price = `Price required`;
    if (Math.abs(lot) > Math.abs(row.netLot || 0))   e.lot   = `Max: ${Math.abs(row.netLot)}`;
    if (Math.abs(qty) > Math.abs(row.netQty || 0))   e.qty   = `Max: ${Math.abs(row.netQty)}`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const summaryPayload = () => ({
    lot,
    qty,
    price: closeAtPrice,
    marketType,
    oneQtySize,
  });

  const renderStepper = ({ label, value, onMinus, onPlus, onChange, disabled, err, readOnly }) => (
    <div className="pr-mt5-mod__field">
      <div className="pr-mt5-mod__label">{label}</div>
      <div className={`pr-mt5-mod__stepper${disabled || readOnly ? " is-disabled" : ""}`}>
        <button type="button" className="pr-mt5-mod__step" disabled={disabled || readOnly || loading} onClick={onMinus} aria-label={`Decrease ${label}`}>−</button>
        <input
          type="number"
          className="pr-mt5-mod__input"
          value={value}
          disabled={disabled || readOnly || loading}
          onChange={onChange}
        />
        <button type="button" className="pr-mt5-mod__step" disabled={disabled || readOnly || loading} onClick={onPlus} aria-label={`Increase ${label}`}>+</button>
      </div>
      {err ? <div className="pr-mt5-mod__err">{err}</div> : null}
    </div>
  );

  if (mobileMt5) {
    return (
      <ClosePositionPortal onBackdropClick={onClose} loading={loading} darkTheme={darkTheme} mobileMt5>
        <div className="pr-mt5-mod">
          <div className="pr-mt5-mod__head">
            <div className="pr-mt5-mod__titles">
              <span className="pr-mt5-mod__symbol">{symbolTitle}</span>
              {expiryLabel ? <span className="pr-mt5-mod__expiry">{expiryLabel}</span> : null}
            </div>
            <button type="button" className="pr-mt5-mod__dismiss" disabled={loading} onClick={onClose} aria-label="Close">✕</button>
          </div>
          <div className="pr-mt5-mod__summary">{summaryLine}</div>
          <div className="pr-mt5-mod__body">
            <div className="pr-mt5-mod__radios">
              {["market", "limit"].map((type) => (
                <label key={type} className={`pr-mt5-mod__radio${marketType === type ? " is-active" : ""}`}>
                  <input
                    type="radio"
                    checked={marketType === type}
                    onChange={() => setMarketType(type)}
                  />
                  {type === "market" ? t("market") : "Limit"}
                </label>
              ))}
            </div>
            <div className="pr-mt5-mod__grid">
              {renderStepper({
                label: t("lot"),
                value: lot,
                onMinus: () => bumpLot(-1),
                onPlus: () => bumpLot(1),
                onChange: (e) => setLotLinked(+e.target.value),
                err: errors.lot,
              })}
              {renderStepper({
                label: t("qty"),
                value: qty,
                onMinus: () => bumpQty(-1),
                onPlus: () => bumpQty(1),
                onChange: (e) => setQty(+e.target.value),
                disabled: row.segmentId === 2 || row.segmentId === 3,
                err: errors.qty,
              })}
            </div>
            <div className="pr-mt5-mod__grid pr-mt5-mod__grid--price">
              {renderStepper({
                label: t("price"),
                value: price,
                onMinus: () => bumpPrice(-1),
                onPlus: () => bumpPrice(1),
                onChange: (e) => setPrice(+e.target.value),
                readOnly: marketType === "market",
                err: errors.price,
              })}
            </div>
            <div className="pr-mt5-mod__footer">
              <button type="button" className="pr-mt5-mod__btn pr-mt5-mod__btn--modify" disabled>
                {t("modify")}
              </button>
              <button
                type="button"
                className="pr-mt5-mod__btn pr-mt5-mod__btn--close"
                disabled={loading}
                onClick={() => {
                  if (!validate()) return;
                  onConfirm(summaryPayload());
                }}
              >
                {loading ? "Closing…" : closeBtnLabel}
              </button>
            </div>
          </div>
        </div>
      </ClosePositionPortal>
    );
  }

  return (
    <ClosePositionPortal onBackdropClick={onClose} loading={loading} darkTheme={darkTheme}>
        <div className="pr-close-pop__head">
          <span className="pr-close-pop__title">
            {phase === "confirm" ? "Confirm close" : "✕ Close Position"}
          </span>
          <button type="button" className="pr-close-pop__close" disabled={loading} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {phase === "edit" ? (
          <>
        <div className="pr-close-pop__body">
          <div className="pr-close-pop__meta">
            <span><span className="pr-close-pop__meta-label">Client: </span><strong>{row.clientName}</strong></span>
            <span><span className="pr-close-pop__meta-label">Script: </span><strong>{row.script}</strong></span>
          </div>

          <div className="pr-close-pop__radios">
            {['market','limit'].map(t => (
              <label key={t} className="pr-close-pop__radio">
                <input type="radio" checked={marketType===t}
                  onChange={() => { setMarketType(t); if (t==='market') setPrice(row.ltp); }} />
                {t === 'market' ? 'Market' : 'Limit / BuyStop'}
              </label>
            ))}
          </div>

          <div className="pr-close-pop__row2">
            <div className="pr-close-pop__field">
              <div className="pr-close-pop__label">LOT *</div>
              <input type="number" className="pr-close-pop__input" min={0} max={Math.abs(row.netLot||0)} value={lot}
                onChange={e => {
                  const v = +e.target.value;
                  setLot(v);
                  setQty(Math.round(Math.abs(v * oneQtySize)));
                }} />
              {errors.lot && <div className="pr-close-pop__err">{errors.lot}</div>}
            </div>
            <div className="pr-close-pop__field">
              <div className="pr-close-pop__label">QTY *</div>
              <input type="number" className="pr-close-pop__input" min={1} max={Math.abs(row.netQty||0)} value={qty}
                disabled={row.segmentId===2 || row.segmentId===3}
                onChange={e => setQty(+e.target.value)} />
              {errors.qty && <div className="pr-close-pop__err">{errors.qty}</div>}
            </div>
          </div>

          <div className="pr-close-pop__field">
            <div className="pr-close-pop__label">PRICE *</div>
            <input type="number" className="pr-close-pop__input" min={0} value={price}
              disabled={marketType==='market'}
              onChange={e => setPrice(+e.target.value)} />
            {errors.price && <div className="pr-close-pop__err">{errors.price}</div>}
          </div>
        </div>
        <div className="pr-close-pop__footer">
          <button type="button" className="pr-close-pop__btn pr-close-pop__btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="pr-close-pop__btn pr-close-pop__btn--danger" disabled={loading}
            onClick={() => { if (validate()) setPhase("confirm"); }}>
            Close Position
          </button>
        </div>
          </>
        ) : (
          <>
            <div className="pr-close-pop__body">
              <div className="pr-close-pop__confirm-title">Are you sure?</div>
              <div className="pr-close-pop__confirm-text">
                Lot: <strong>{lot}</strong>, Qty: <strong>{qty}</strong>, Price: <strong>{price}</strong>
              </div>
            </div>
            <div className="pr-close-pop__footer">
              <button type="button" className="pr-close-pop__btn pr-close-pop__btn--outline-danger" disabled={loading}
                onClick={() => setPhase("edit")}>
                Cancel
              </button>
              <button type="button" className="pr-close-pop__btn pr-close-pop__btn--confirm" disabled={loading}
                onClick={() => onConfirm(summaryPayload())}>
                {loading ? "Closing…" : "Yes, Close it!"}
              </button>
            </div>
          </>
        )}
    </ClosePositionPortal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIRM ALL CLOSE MODAL
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmAllModal({ count, loading, onClose, onConfirm, darkTheme = false }) {
  const { t } = useAppLanguage();
  return (
    <ClosePositionPortal onBackdropClick={onClose} loading={loading} darkTheme={darkTheme}>
        <div className="pr-close-pop__head">
          <span className="pr-close-pop__title">⚠ {t("closeAllPositions")}</span>
          <button type="button" className="pr-close-pop__close" onClick={onClose} aria-label={t("close")}>✕</button>
        </div>
        <div className="pr-close-pop__body pr-close-pop__body--center">
          <div className="pr-close-pop__confirm-text">
            {t("close")} <strong className="pr-close-pop__danger-text">{count}</strong> {t("positions")}?
          </div>
          <div className="pr-close-pop__hint">This action cannot be undone.</div>
        </div>
        <div className="pr-close-pop__footer">
          <button type="button" className="pr-close-pop__btn pr-close-pop__btn--ghost" onClick={onClose}>{t("cancel")}</button>
          <button type="button" className="pr-close-pop__btn pr-close-pop__btn--danger" disabled={loading} onClick={onConfirm}>
            {loading ? t("loading") : t("confirm")}
          </button>
        </div>
    </ClosePositionPortal>
  );
}

function hasVal(v) {
  if (v == null || v === "") return false;
  if (typeof v === "number" && !Number.isFinite(v)) return false;
  return true;
}

function fmtTradeSummaryNum(n, digits = 2) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function fmtTradePnl(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Build bottom-sheet model from an open position (History-sheet UI, Trade fields). */
function buildTradeMt5Sheet(row, orderTypeForRow, segmentLabel) {
  if (!row) return null;
  const mtmVal = (row.profit ?? row.mtm ?? 0) * (row.currencyRate || 1);
  const mtmNum = Number(mtmVal);
  const side = String(orderTypeForRow?.(row) || "").toLowerCase() || "—";
  const sTone = side.includes("buy") ? "buy" : side.includes("sell") ? "sell" : "flat";
  const priceDp = row.digitAfterPoint ?? 2;
  const openPx = Number(row.openPrice || 0).toFixed(priceDp);
  const curPx = Number(row.currentPrice ?? row.ltp ?? 0).toFixed(priceDp);
  const lots = row.lots ?? row.Lots;
  const qty = row.qty ?? row.Qty;
  const ticket = row.positionId ?? row.PositionId;
  const openTime = formatOpenPositionTime(row.openTime);
  const symbol = tradeSymbolLabel(row);
  const expiry = tradeExpiryLabel(row);
  const seg =
    typeof segmentLabel === "function" ? segmentLabel(row) : row?.segment || "";
  const lotsTxt = hasVal(lots) ? String(lots) : "";
  const fields = [
    { key: "positionTicket", value: hasVal(ticket) ? String(ticket) : "—" },
    { key: "qty", value: hasVal(qty) ? String(qty) : "—" },
    { key: "openTime", value: openTime || "—" },
    { key: "segment", value: seg || "—" },
  ];
  return {
    id: ticket,
    time: openTime || "",
    symbol,
    expiry,
    chartSymbol: String(row.script || row.symbol || row.displayName || "").trim(),
    side: lotsTxt ? `${side} ${lotsTxt}` : side,
    sTone,
    subtitle: `${openPx} → ${curPx}`,
    rightPrimary: Number.isFinite(mtmNum)
      ? {
          text: fmtTradePnl(mtmNum),
          color: mtmNum > 0 ? "#1e5eff" : mtmNum < 0 ? "#c62828" : "#111",
        }
      : null,
    fields,
    row,
  };
}

/** Same sheet chrome as History — Trade position content. */
function TradeMt5DetailSheet({
  model,
  onClose,
  onChart,
  onNewOrder,
  onModifyClosePosition,
  onClosePosition,
}) {
  const { t, languageId } = useAppLanguage();
  if (!model) return null;
  const lineTone =
    model.sTone === "buy"
      ? " hist-mt5-sheet__line1--buy"
      : model.sTone === "sell"
        ? " hist-mt5-sheet__line1--sell"
        : "";
  const fieldPairs = [];
  const fields = model.fields || [];
  for (let i = 0; i < fields.length; i += 2) {
    fieldPairs.push([fields[i], fields[i + 1] || null]);
  }
  return (
    <div className="hist-mt5-sheet-root" role="presentation">
      <button
        type="button"
        className="hist-mt5-sheet-backdrop"
        aria-label="Close details"
        onClick={onClose}
      />
      <div className="hist-mt5-sheet hist-mt5-sheet--trade" role="dialog" aria-label="Position details">
        <div className="hist-mt5-sheet__id-row">
          <span className="hist-mt5-sheet__time">{model.time || "—"}</span>
          <span className="hist-mt5-sheet__id">#{model.id != null ? model.id : "—"}</span>
          <button
            type="button"
            className="hist-mt5-sheet__dismiss"
            onClick={onClose}
            aria-label="Close details"
          >
            ✕
          </button>
        </div>
        <div className="hist-mt5-sheet__main">
          <div className="hist-mt5-sheet__left">
            <div className={`hist-mt5-sheet__line1${lineTone}`}>
              <span className="hist-mt5-sheet__symbol">{model.symbol}</span>
              {model.side ? (
                <>
                  <span className="hist-mt5-sheet__sep"> </span>
                  <span className={`hist-mt5-sheet__side hist-mt5-sheet__side--${model.sTone || "flat"}`}>{tSide(model.side, languageId)}</span>
                </>
              ) : null}
            </div>
            {model.expiry ? (
              <div className="hist-mt5-sheet__expiry">{model.expiry}</div>
            ) : null}
            {model.subtitle ? (
              <div className="hist-mt5-sheet__line2">{model.subtitle}</div>
            ) : null}
          </div>
          {model.rightPrimary ? (
            <div
              className="hist-mt5-sheet__right"
              style={{ color: model.rightPrimary.color }}
            >
              {model.rightPrimary.text}
            </div>
          ) : null}
        </div>
        {fieldPairs.length ? (
          <div className="hist-mt5-sheet__fields">
            {fieldPairs.map((pair, idx) => (
              <div key={`tfp-${idx}`} className="hist-mt5-sheet__field-row">
                {pair.map((f, j) =>
                  f ? (
                    <div key={f.key || f.label} className="hist-mt5-sheet__field">
                      <span className="hist-mt5-sheet__field-label">{t(f.key || f.label)}:</span>
                      <span className="hist-mt5-sheet__field-value">{f.value}</span>
                    </div>
                  ) : (
                    <div key={`empty-${j}`} className="hist-mt5-sheet__field" />
                  )
                )}
              </div>
            ))}
          </div>
        ) : null}
        <div className="hist-mt5-sheet__actions">
          {onClosePosition ? (
            <button
              type="button"
              className="hist-mt5-sheet__action hist-mt5-sheet__action--danger"
              onClick={() => onClosePosition(model)}
            >
              {t("closePosition")}
            </button>
          ) : null}
          <button
            type="button"
            className="hist-mt5-sheet__action"
            disabled={!model.chartSymbol}
            onClick={() => onNewOrder?.(model)}
          >
            {t("newOrder")}
          </button>
          {onModifyClosePosition ? (
            <button
              type="button"
              className="hist-mt5-sheet__action"
              onClick={() => onModifyClosePosition(model)}
            >
              {t("modifyPosition")}
            </button>
          ) : null}
          <button
            type="button"
            className="hist-mt5-sheet__action"
            disabled={!model.chartSymbol}
            onClick={() => onChart?.(model)}
          >
            {t("chart")}
          </button>
        </div>
      </div>
    </div>
  );
}

function PositionAdvRow({
  row,
  selected,
  onSelect,
  canClose,
  onClose,
  orderTypeForRow,
  C,
}) {
  const lastTapRef = useRef(0);
  const selectTimerRef = useRef(null);
  const { languageId } = useAppLanguage();

  const mtmVal = (row.profit ?? row.mtm ?? 0) * (row.currencyRate || 1);
  const mtmColor = mtmVal > 0 ? "#1e5eff" : mtmVal < 0 ? "#c62828" : C.tx;
  const side = orderTypeForRow(row);
  const sideLower = String(side || "").toLowerCase();
  const priceDp = row.digitAfterPoint ?? 2;
  const openPx = Number(row.openPrice || 0).toFixed(priceDp);
  const curPx = Number(row.currentPrice ?? row.ltp ?? 0).toFixed(priceDp);
  const lots = row.lots ?? row.Lots;

  const handleActivate = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 320) {
      lastTapRef.current = 0;
      if (selectTimerRef.current) {
        clearTimeout(selectTimerRef.current);
        selectTimerRef.current = null;
      }
      if (canClose && row.netQty !== 0) onClose(row);
      return;
    }
    lastTapRef.current = now;
    if (selectTimerRef.current) clearTimeout(selectTimerRef.current);
    selectTimerRef.current = setTimeout(() => {
      selectTimerRef.current = null;
      onSelect?.(row);
    }, 280);
  };

  return (
    <article
      className={`pr-adv-row pr-adv-row--mt5 pr-adv-row--${sideLower}${
        selected ? " is-selected" : ""
      }`}
      onClick={handleActivate}
      onDoubleClick={(e) => {
        e.preventDefault();
        if (canClose && row.netQty !== 0) onClose(row);
      }}
      role="button"
      tabIndex={0}
      aria-selected={selected}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate();
        }
      }}
    >
      <div className="pr-adv-row__mt5-main">
        <div className="pr-adv-row__mt5-body">
          <div className="pr-adv-row__mt5-line1">
            <span className="pr-adv-row__symbol">{tradeSymbolLabel(row)}</span>
            <span className={`pr-adv-row__mt5-sidewrap pr-adv-row__mt5-sidewrap--${sideLower}`}>
              <span className="pr-adv-row__mt5-sep"> </span>
              <span className="pr-adv-row__side-text">{tSide(sideLower, languageId)}</span>
              {hasVal(lots) ? <span className="pr-adv-row__mt5-lot">{lots}</span> : null}
            </span>
          </div>
          <div className="pr-adv-row__mt5-line2">
            <span className="pr-adv-row__mt5-prices">
              {openPx}
              <span className="pr-adv-row__mt5-arrow">{" → "}</span>
              {curPx}
            </span>
          </div>
        </div>

        <div className="pr-adv-row__mt5-right">
          <strong className="pr-adv-row__mt5-pnl" style={{ color: mtmColor }}>
            {fmtTradePnl(mtmVal)}
          </strong>
        </div>
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function PositionReport({ embedded = false, hideFilters = false, darkTheme = false } = {}) {
  const dispatch = useDispatch();
  const { t } = useAppLanguage();
  const isMobileViewport = useMobileViewport();
  const showMobileCards = !embedded && isMobileViewport;
  useBrokerTimeZoneId();

  // ── Redux ─────────────────────────────────────────────────────────────────
  const {
    filteredPositionReportList = [],
    positionReportList = [],
    duplicatePositionReportList = [],
    positionReportLoading = false,
    totalRecordPositionReport = 0,
    currentPagePositionReport = 1,
    pageSizePositionReport = 10000,
    positionReportFilterValue = {},
    positionReportSearchText = "",
    closePositonSelected = [],
    isPositionCloseLoading = false,
    positionCloseConfirmDialog = false,
    positionCloseData = null,
    isClosePosition = false,
    liveRateFromSocket = null,
    positionDisplayNames = [],
  } = useSelector(({ positionReport }) => positionReport || {});

  const { isAddedOrder = false } = useSelector(({ marketWatch }) => marketWatch || {});
  const isOrderBookUpdated = useSelector((s) => s.orderbook?.isOrderBookUpdated);
  const marketWatchList = useSelector((s) => s.marketWatch?.marketWatchList);
  const { marginSummary, marginSummaryLoading } = useSelector((s) => s.margin || {});

  const { valansList = [] } = useSelector(({ valan }) => valan || {});

  // ── Session ───────────────────────────────────────────────────────────────
  const { userId, userName, userRole, token } = getSessionData();
  const masterRoles = ['Super Admin','Admin','Super Master','Master'];
  const isMasterRole = masterRoles.includes(userRole);
  const isPartner    = userRole === 'Sub Broker' || userRole === 'Partner';
  const isAdminRole  = masterRoles.includes(userRole);
  const canCancelPending = !["partner", "sub broker"].includes(String(userRole || "").toLowerCase());

  // Pending orders under open positions (MT5 Trade panel) — chart dock only.
  const [pendingOrders, setPendingOrders] = useState([]);
  const [cancellingPendingId, setCancellingPendingId] = useState(null);

  // ── Auth token ────────────────────────────────────────────────────────────
  useEffect(() => { if (token) setAuthToken(token); }, []);

  // ── Dropdown data ─────────────────────────────────────────────────────────
  const [segmentOptions, setSegmentOptions] = useState([]);
  const [valanOptions,   setValanOptions]   = useState([]);
  const [symbolOptions,  setSymbolOptions]  = useState([]);
  const [clientOptions,  setClientOptions]  = useState([]);
  const [loadingSeg,     setLoadingSeg]     = useState(false);
  const [loadingCli,     setLoadingCli]     = useState(false);

  // ── Filter state ──────────────────────────────────────────────────────────
  /** Full page + chart-docked: legacy defaults — Net Wise + Outstanding + auto-fetch (embedded effect locks disptype). */
  const [filters, setFilters] = useState(() => ({
    crtype     : "net",
    disptype   : "outstanding",
    reporttype : "client",
    segment    : null,
    valan      : null,
    clientcode : null,
    symbol     : null,
    subbroker  : null,
    exdate     : new Date(),
    isExDateSel: false,
  }));
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const positionReportSearchTextRef = useRef(positionReportSearchText);
  positionReportSearchTextRef.current = positionReportSearchText;
  const fullPageAutoFetchedRef = useRef(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  /** Docked panel: first row = all segments (API segmentId null). Full page uses API list only. */
  const embeddedSegmentOptions = useMemo(
    () => [{ label: "All segments", value: null }, ...segmentOptions],
    [segmentOptions]
  );

  // Match react-select option references after remount / reload (avoids blank Segment/Valan labels)
  const segmentSelectValue = useMemo(() => {
    if (!filters.segment) return null;
    const pool = embedded ? embeddedSegmentOptions : segmentOptions;
    return pool.find((o) => o.value === filters.segment.value) ?? filters.segment;
  }, [filters.segment, segmentOptions, embedded, embeddedSegmentOptions]);

  const valanSelectValue = useMemo(() => {
    if (!filters.valan) return null;
    const fv = filters.valan.value;
    const fid = fv != null && typeof fv === "object" && "id" in fv ? fv.id : fv;
    const found = valanOptions.find((o) => {
      const ov = o.value;
      const oid = ov != null && typeof ov === "object" && "id" in ov ? ov.id : ov;
      return fid != null && oid != null && String(fid) === String(oid);
    });
    return found ?? filters.valan;
  }, [filters.valan, valanOptions]);

  const segmentLabel = useCallback(
    (row) =>
      resolveSegmentLabel(
        row?.segmentId ?? row?.SegmentId,
        segmentOptions,
        row?.segment ?? row?.Segment
      ),
    [segmentOptions]
  );

  // Chart dock + mobile MT5 Trade: always Net Wise + Outstanding (no Day/Net UI)
  useEffect(() => {
    if (!embedded && !showMobileCards) return;
    setFilters((f) => ({
      ...f,
      crtype: "net",
      disptype: "outstanding",
    }));
  }, [embedded, showMobileCards]);

  // ── Sort / UI state ───────────────────────────────────────────────────────
  const [sortObj,       setSortObj]       = useState(null);
  const [showConfirmAll,setShowConfirmAll] = useState(false);
  const [searchInput,   setSearchInput]   = useState('');

  // Embedded: no search UI — keep server search empty
  useEffect(() => {
    if (!embedded) return;
    dispatch(setPositionReportSearchText(""));
    setSearchInput("");
    positionReportSearchTextRef.current = "";
  }, [embedded, dispatch]);

  // ── Load segments + clients on mount ─────────────────────────────────────
  useEffect(() => {
    dispatch(getValansList());

    setLoadingSeg(true);
    api.get('master/segment/get-segments')
      .then(({ data }) => setSegmentOptions(
        (data?.data || []).map(s => ({ label:s.name, value:s.id, digitAfterPoint:s.digitAfterPoint }))
      ))
      .catch(() => {})
      .finally(() => setLoadingSeg(false));

    if (userRole !== 'User') {
      setLoadingCli(true);
      api.get('common/get-account-names-by-rolename/User')
        .then(({ data }) => setClientOptions(
          (data?.data || []).map(c => ({ value:c.value??c.id, label:c.label??c.name }))
        ))
        .catch(() => {})
        .finally(() => setLoadingCli(false));
    }

    // Do not clear position lists / totals here: full page and chart-docked instances
    // share Redux; unmounting one route would wipe data for the other.
    return () => {
      dispatch(setClosePositonSelected([]));
      dispatch(setPositionDisplayNames([]));
    };
  }, []);

  // ── Build valan options when valansList arrives ───────────────────────────
  useEffect(() => {
    if (!valansList || valansList.length === 0) return;
    const opts = valansList
      .filter(v => v.status == 1)
      .map(v => ({ label:v.name, value:v }));
    setValanOptions(opts);
    if (opts.length === 0) return;
    setFilters((f) => (f.valan ? f : { ...f, valan: opts[0] }));
  }, [valansList]);

  // ── Load symbols when segment changes ────────────────────────────────────
  useEffect(() => {
    if (!filters.segment) { setSymbolOptions([]); return; }
    api.get(`market-watch/get-symbols-by-segment/${filters.segment.value}`)
      .then(({ data }) => setSymbolOptions(
        (data?.data || []).map(s => ({ value:s.value??s.id, label:s.label??s.name }))
      ))
      .catch(() => setSymbolOptions([]));
  }, [filters.segment]);

  // ── Auto-reload after close / rollover (isClosePosition watcher) ─────────
  useEffect(() => {
    if (!isClosePosition) return;
    dispatch(getPositionReportList({
      ...positionReportFilterValue,
      searchText : positionReportSearchText,
      pageNo     : 1,
      pageSize   : pageSizePositionReport || 10000,
      appendData : false,
    }));
    dispatch(setIsClosePosition(false));
  }, [
    isClosePosition,
    dispatch,
    positionReportFilterValue,
    positionReportSearchText,
    pageSizePositionReport,
  ]);

  // ── Socket: subscribe + live P&L ─────────────────────────────────────────
  useEffect(() => {
    const socket = getSocket();
    const onMessage = (data) => {
      if (data && Object.keys(data).length > 0) {
        dispatch(setSocketData(data));
      }
    };
    socket.on("message", onMessage);
    return () => {
      socket.off("message", onMessage);
    };
  }, [dispatch]);

  useEffect(() => {
    if (userRole !== "User") return undefined;
    const names = positionDisplayNames;
    if (!names?.length) return undefined;
    const socket = getSocket();
    const currentSymbols = [...new Set(names)].filter(Boolean);
    if (socket?.connected && currentSymbols.length) {
      socket.emit("room", currentSymbols.toString());
    }
    return () => {
      if (socket?.connected && currentSymbols.length) {
        socket.emit("leave_room", currentSymbols.toString());
      }
    };
  }, [userRole, positionDisplayNames]);

  useEffect(() => {
    const tick = liveRateFromSocket || {};
    if (!tick.Script || !filteredPositionReportList?.length) return;

    const updated = filteredPositionReportList.map((row) =>
      applyLiveQuoteToOpenPosition(row, tick)
    );
    dispatch(setfilteredPositionReportList(updated));
    dispatch(setPositionReportList(updated));
  }, [liveRateFromSocket]);

  // ── Sort — sync duplicateList → filteredList ─────────────────────────────
  useEffect(() => {
    if (duplicatePositionReportList?.length > 0) {
      let list = [...duplicatePositionReportList];
      if (sortObj) list = _.orderBy(list, [sortObj.id], [sortObj.desc?'desc':'asc']);
      dispatch(setfilteredPositionReportList(list));
    }
  }, [duplicatePositionReportList]);

  // ── GET POSITION ──────────────────────────────────────────────────────────
  const handleGetPosition = useCallback(() => {
    const f = filtersRef.current;
    const payload = {
      ...f,
      userId,
      searchText : '',
      pageNo     : 1,
      pageSize   : pageSizePositionReport || 10000,
      appendData : false,
    };
    dispatch(setPositionReportFilterValue(payload));
    dispatch(setStartPagePositionReport(1));
    dispatch(setPositionReportSearchText(''));
    dispatch(setfilteredPositionReportList([]));
    dispatch(getPositionReportList(payload));
    setSearchInput('');
    dispatch(setClosePositonSelected([]));
  }, [userId, pageSizePositionReport, dispatch]);

  /** Chart-docked: auto-refresh without clearing the table first (less flicker while loading). */
  const fetchEmbeddedPosition = useCallback(() => {
    const f = filtersRef.current;
    if (!f.valan) return;
    const payload = {
      ...f,
      userId,
      searchText: positionReportSearchTextRef.current,
      pageNo: 1,
      pageSize: pageSizePositionReport || 10000,
      appendData: false,
    };
    dispatch(setPositionReportFilterValue(payload));
    dispatch(setStartPagePositionReport(1));
    dispatch(getPositionReportList(payload));
  }, [userId, pageSizePositionReport, dispatch]);

  const embeddedFetchKey = useMemo(() => {
    if (!embedded || !filters.valan) return "";
    const v = filters.valan.value;
    const valanPart = v != null && typeof v === "object" && "id" in v ? v.id : v;
    const segPart =
      filters.segment != null && filters.segment.value != null && filters.segment.value !== ""
        ? String(filters.segment.value)
        : "ALL";
    return [segPart, valanPart].join("\u0001");
  }, [embedded, filters.segment, filters.valan]);

  // Embedded: refetch when filters change (no Get button). segment omitted / cleared = all segments.
  useEffect(() => {
    if (!embeddedFetchKey || loadingSeg) return;
    const f = filtersRef.current;
    if (!f.valan) return;
    dispatch(setClosePositonSelected([]));
    dispatch(setPositionReportSearchText(""));
    setSearchInput("");
    positionReportSearchTextRef.current = "";
    fetchEmbeddedPosition();
  }, [embeddedFetchKey, fetchEmbeddedPosition, loadingSeg, dispatch]);

  const fetchEmbeddedRef = useRef(fetchEmbeddedPosition);
  fetchEmbeddedRef.current = fetchEmbeddedPosition;

  const fetchPendingOrders = useCallback(async () => {
    if (!embedded) return;
    const sess = getSessionData();
    const uid = Number(sess.userId || sess.id || sess.UserId || userId || 0);
    if (!uid) return;
    try {
      // Prefer the same path as Order Book (proven mapping).
      const action = await dispatch(
        getOrderBookList({
          isCompleted: "pending",
          pageNo: 1,
          pageSize: 200,
          userId: uid,
          appendData: false,
        })
      );
      if (getOrderBookList.fulfilled.match(action)) {
        const records = action.payload?.data?.records || [];
        setPendingOrders(records);
        return;
      }
      // Fallback raw API
      const { data: res } = await api.post("orders/get-pending", {
        userId: uid,
        pageNo: 1,
        pageSize: 200,
        segmentId: null,
        clientId: null,
        symbol: null,
        searchText: "",
      });
      const payload = res?.data ?? res?.Data ?? res;
      const records =
        payload?.records ||
        payload?.Records ||
        (Array.isArray(payload) ? payload : []);
      setPendingOrders(Array.isArray(records) ? records : []);
    } catch {
      /* keep prior */
    }
  }, [embedded, userId, dispatch]);

  const fetchPendingRef = useRef(fetchPendingOrders);
  fetchPendingRef.current = fetchPendingOrders;

  useEffect(() => {
    if (!embedded) return;
    fetchPendingOrders();
  }, [embedded, fetchPendingOrders, embeddedFetchKey, isAddedOrder, isOrderBookUpdated]);

  const onCancelPendingOrder = useCallback(
    (orderId) => {
      if (!orderId || !canCancelPending) return;
      Swal.fire({
        title: "Are you sure?",
        text: "Cancel this pending order?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Cancel Order",
        cancelButtonText: "No",
        confirmButtonColor: "#885b24",
        cancelButtonColor: "#d33",
      }).then(async (r) => {
        if (!r.isConfirmed) return;
        setCancellingPendingId(orderId);
        try {
          const res = await dispatch(deleteOrder({ id: orderId }));
          if (res?.meta?.requestStatus === "fulfilled") {
            fetchPendingRef.current?.();
          }
        } finally {
          setCancellingPendingId(null);
        }
      });
    },
    [canCancelPending, dispatch]
  );

  // ── Auto-reload after market trade fill (isAddedOrder watcher) ───────────
  useEffect(() => {
    if (!isAddedOrder) return;
    if (embedded) {
      fetchEmbeddedRef.current?.();
      fetchPendingRef.current?.();
    } else if (positionReportFilterValue && Object.keys(positionReportFilterValue).length > 0) {
      dispatch(getPositionReportList({
        ...positionReportFilterValue,
        searchText : positionReportSearchText,
        pageNo     : 1,
        pageSize   : pageSizePositionReport || 10000,
        appendData : false,
      }));
    }
    dispatch(setIsAddedOrder(false));
  }, [
    isAddedOrder,
    embedded,
    dispatch,
    positionReportFilterValue,
    positionReportSearchText,
    pageSizePositionReport,
  ]);

  /** Docked panel: periodic + tab-focus refresh so new trades show without manual reload */
  useEffect(() => {
    if (!embedded) return undefined;
    const tick = () => {
      const f = filtersRef.current;
      if (!f.valan) return;
      fetchEmbeddedRef.current();
      fetchPendingRef.current?.();
    };
    const id = window.setInterval(tick, 5000);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [embedded]);

  // Full page: first open — auto "Get Position" once valan is ready (same as fxbazzar_frontend GetPositionReportForm).
  useEffect(() => {
    if (embedded) return;
    if (loadingSeg || valanOptions.length === 0) return;
    const f = filtersRef.current;
    if (!f.valan) return;
    if (fullPageAutoFetchedRef.current) return;
    fullPageAutoFetchedRef.current = true;

    const payload = {
      ...f,
      userId,
      searchText: "",
      pageNo: 1,
      pageSize: pageSizePositionReport || 10000,
      appendData: false,
    };
    dispatch(setPositionReportFilterValue(payload));
    dispatch(setStartPagePositionReport(1));
    dispatch(setPositionReportSearchText(""));
    dispatch(setfilteredPositionReportList([]));
    dispatch(getPositionReportList(payload));
    setSearchInput("");
    dispatch(setClosePositonSelected([]));
  }, [embedded, loadingSeg, valanOptions.length, userId, pageSizePositionReport, dispatch]);

  // ── EXPORT ────────────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    if (!filteredPositionReportList?.length) return;
    dispatch(exportPositionReport({
      ...positionReportFilterValue,
      pageNo     : 1,
      pageSize   : pageSizePositionReport || 10000,
      searchText : positionReportSearchText,
    }));
  }, [positionReportFilterValue, pageSizePositionReport, positionReportSearchText, filteredPositionReportList, dispatch]);

  // ── DEBOUNCED SEARCH ──────────────────────────────────────────────────────
  const debouncedSearch = useCallback(
    _.debounce((value) => {
      dispatch(setPositionReportSearchText(value));
      dispatch(setStartPagePositionReport(1));
      dispatch(getPositionReportList({
        ...positionReportFilterValue,
        searchText : value,
        pageNo     : 1,
        pageSize   : pageSizePositionReport || 10000,
        appendData : false,
      }));
    }, 800),
    [positionReportFilterValue, pageSizePositionReport]
  );

  // ── SORT ──────────────────────────────────────────────────────────────────
  const handleSort = useCallback((key) => {
    const sortKey = key === "orderType" ? "netQty" : key;
    let ns = { id:sortKey, desc:false };
    if (sortObj?.id === sortKey) {
      if (!sortObj.desc) ns = { id:sortKey, desc:true };
      else ns = null;
    }
    setSortObj(ns);
    let list = [...(filteredPositionReportList||[])];
    if (ns) list = _.orderBy(list, [ns.id], [ns.desc?'desc':'asc']);
    else    list = [...(duplicatePositionReportList||[])];
    dispatch(setfilteredPositionReportList(list));
  }, [sortObj, filteredPositionReportList, duplicatePositionReportList, dispatch]);

  // ── CHECKBOX single row ───────────────────────────────────────────────────
  const handleCheck = useCallback((row) => {
    const updated     = (filteredPositionReportList||[]).map(r => r.rowId===row.rowId ? { ...r, isCheck:!r.isCheck } : r);
    const updatedMain = (positionReportList||[]).map(r => r.rowId===row.rowId ? { ...r, isCheck:!r.isCheck } : r);
    dispatch(setfilteredPositionReportList(updated));
    dispatch(setPositionReportList(updatedMain));
    dispatch(setClosePositonSelected(updated.filter(r => r.isCheck && r.netQty !== 0)));
  }, [filteredPositionReportList, positionReportList, dispatch]);

  // ── CHECKBOX select all ───────────────────────────────────────────────────
  const handleCheckAll = useCallback((checked) => {
    const updated     = (filteredPositionReportList||[]).map(r => r.netQty!==0 ? { ...r, isCheck:checked } : r);
    const updatedMain = (positionReportList||[]).map(r => r.netQty!==0 ? { ...r, isCheck:checked } : r);
    dispatch(setfilteredPositionReportList(updated));
    dispatch(setPositionReportList(updatedMain));
    dispatch(setClosePositonSelected(checked ? updated.filter(r => r.isCheck && r.netQty!==0) : []));
  }, [filteredPositionReportList, positionReportList, dispatch]);

  /** Keep Modify/Close modal on live LTP/MTM from the open-position list (socket ticks). */
  const livePositionCloseRow = useMemo(() => {
    if (!positionCloseData) return null;
    const list = filteredPositionReportList || [];
    const live = list.find(
      (r) =>
        r.rowId === positionCloseData.rowId ||
        (positionCloseData.positionId != null &&
          r.positionId === positionCloseData.positionId)
    );
    if (!live) return positionCloseData;
    return {
      ...positionCloseData,
      ltp: live.ltp ?? live.currentPrice ?? positionCloseData.ltp,
      currentPrice:
        live.currentPrice ?? live.ltp ?? positionCloseData.currentPrice,
      profit: live.profit ?? positionCloseData.profit,
      mtm: live.mtm ?? positionCloseData.mtm,
      currencyRate: live.currencyRate ?? positionCloseData.currencyRate,
    };
  }, [positionCloseData, filteredPositionReportList]);

  // ── SINGLE CLOSE (confirm step is inside ClosePositionModal — avoids SweetAlert over modal hang) ──
  const handleSingleClose = useCallback(async (formVals) => {
    const base = livePositionCloseRow || positionCloseData;
    const act = await dispatch(closePosition({
      ...base,
      ...formVals,
      ltp: formVals?.price ?? base?.ltp,
      oldNetQty : base.netQty,
    }));
    if (act.meta.requestStatus === "fulfilled") {
      dispatch(setPositionCloseConfirmDialog(false));
      return;
    }
    // Failure toast (MT5 Done error) is dispatched by closePosition; keep Modify open so user can retry.
  }, [positionCloseData, livePositionCloseRow, dispatch]);

  // ── BULK CLOSE ────────────────────────────────────────────────────────────
  const handleCloseAll = useCallback(async () => {
    await dispatch(saveAllClosePosition(closePositonSelected));
    dispatch(setClosePositonSelected([]));
    setShowConfirmAll(false);
  }, [closePositonSelected, dispatch]);

  // ── ROLLOVER ──────────────────────────────────────────────────────────────
  const handleRollover = useCallback(async () => {
    if (!closePositonSelected || closePositonSelected.length === 0) {
      await alertSelectPositionFirst();
      return;
    }
    const result = await confirmRolloverPositions(closePositonSelected.length);
    if (!result.isConfirmed) return;
    dispatch(rollOverPosition(closePositonSelected));
  }, [closePositonSelected, dispatch]);

  // ── INFINITE SCROLL ───────────────────────────────────────────────────────
  const handleScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop > el.clientHeight + 100) return;
    const loaded = filteredPositionReportList?.length || 0;
    if (loaded >= totalRecordPositionReport || positionReportLoading) return;
    const next = currentPagePositionReport + 1;
    dispatch(setStartPagePositionReport(next));
    dispatch(getPositionReportList({
      ...positionReportFilterValue,
      pageNo     : next,
      pageSize   : pageSizePositionReport || 10000,
      searchText : positionReportSearchText,
      appendData : true,
    }));
  }, [filteredPositionReportList, totalRecordPositionReport, positionReportLoading,
      currentPagePositionReport, positionReportFilterValue, pageSizePositionReport,
      positionReportSearchText, dispatch]);

  // ── TOTAL P&L (MT5 Trade strip — right side; no lot/qty totals) ─────────────
  const totals = useMemo(() => {
    const data = filteredPositionReportList || [];
    if (!data.length) return null;
    const tMTM = data.reduce((s, r) => s + ((r.profit ?? r.mtm ?? 0) * (r.currencyRate || 1)), 0);
    return { tMTM };
  }, [filteredPositionReportList]);

  /** Live margin for chart dock + mobile MT5 Position page. */
  const liveMarginSummary = useMemo(() => {
    if (!embedded && !showMobileCards) return null;
    const livePnl = sumLivePositionPnl(filteredPositionReportList);
    if (filteredPositionReportList?.length) {
      return applyLivePnlToMarginSummary(marginSummary, livePnl);
    }
    return marginSummary;
  }, [embedded, showMobileCards, marginSummary, filteredPositionReportList]);

  const [selectedTradeSheetId, setSelectedTradeSheetId] = useState(null);

  // Mobile Position page: ensure margin summary is loaded for MT5 account block
  useEffect(() => {
    if (!showMobileCards) return;
    dispatch(getMarginSummary());
  }, [showMobileCards, dispatch]);

  // ── Visibility flags ─────────────────────────────────────────────────────
  // Use current filter selection (filters.crtype) — don't wait for post-submit value.
  // positionReportFilterValue starts as {} so its crtype is always undefined until
  // "Get Position" is clicked — that caused Close/Rollover to never show.
  const isCloseButtonVisible = useMemo(() => {
    if (isPartner) return false;
    if (filters.reporttype === 'self') return false;
    if (filters.crtype === 'day' && userRole === 'Super Admin') return true;
    if (filters.crtype === 'net') return true;
    return false;
  }, [filters.crtype, filters.reporttype, isPartner, userRole]);

  const isCheckBoxVisible = isCloseButtonVisible;

  const isRolloverVisible = useMemo(() => {
    if (isPartner) return false;
    if (filters.reporttype === 'self') return false;
    if (filters.crtype === 'day' && userRole === 'Super Admin') return true;
    if (filters.crtype === 'net') return true;
    return false;
  }, [filters.crtype, filters.reporttype, isPartner, userRole]);

  const allChecked = useMemo(() => {
    const checkable = (filteredPositionReportList||[]).filter(r => r.netQty !== 0);
    return checkable.length > 0 && checkable.every(r => r.isCheck);
  }, [filteredPositionReportList]);

  /* Embedded Trade dock (MT5): no checkbox column */
  const prTableColCount =
    (embedded ? 0 : 1) + 9 + (isAdminRole ? 1 : 0);

  const colSortDir = (id) => {
    if (!sortObj) return undefined;
    const sortKey = id === "orderType" ? "netQty" : id;
    if (sortObj.id !== sortKey) return undefined;
    return sortObj.desc ? "desc" : "asc";
  };

  const sortMark = (id) => {
    const dir = colSortDir(id);
    if (!dir) return null;
    return <span className={`pr-th-sort pr-th-sort--${dir}`} aria-hidden />;
  };

  const orderTypeForRow = (row) => {
    const direct = String(row?.orderType ?? row?.buySell ?? row?.side ?? "").trim().toUpperCase();
    if (direct === "BUY" || direct === "SELL") return direct;
    const q = Number(row?.netQty ?? 0);
    if (q > 0) return "BUY";
    if (q < 0) return "SELL";
    return "FLAT";
  };

  const tableRows = filteredPositionReportList || [];

  const selectedTradeSheetModel = useMemo(() => {
    if (!showMobileCards || selectedTradeSheetId == null) return null;
    const row = (tableRows || []).find(
      (r) => String(r.positionId ?? r.PositionId ?? r.rowId) === String(selectedTradeSheetId)
    );
    return buildTradeMt5Sheet(row, orderTypeForRow, segmentLabel);
  }, [showMobileCards, selectedTradeSheetId, tableRows, segmentLabel]);

  const closeTradeSheet = useCallback(() => setSelectedTradeSheetId(null), []);

  const onTradeSheetSelect = useCallback((row) => {
    const id = row?.positionId ?? row?.PositionId ?? row?.rowId;
    if (id == null) return;
    setSelectedTradeSheetId((prev) => (String(prev) === String(id) ? null : id));
  }, []);

  const onTradeSheetChart = useCallback(
    (model) => {
      const symbol = String(model?.chartSymbol || "").trim();
      if (!symbol || typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent("fx-history-open-chart", { detail: { symbol, row: model.row } })
      );
      closeTradeSheet();
    },
    [closeTradeSheet]
  );

  const onTradeSheetNewOrder = useCallback(
    (model) => {
      const symbol = String(model?.chartSymbol || "").trim();
      if (!symbol || typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent("fx-history-new-order", { detail: { symbol, row: model.row } })
      );
      closeTradeSheet();
    },
    [closeTradeSheet]
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const open = Boolean(showMobileCards && selectedTradeSheetModel);
    document.body.classList.toggle("hist-mt5-sheet-open", open);
    return () => document.body.classList.remove("hist-mt5-sheet-open");
  }, [showMobileCards, selectedTradeSheetModel]);

  const tradeSummaryLeft = useMemo(() => {
    const s = liveMarginSummary;
    return [
      { key: "balance", label: `${t("balance")}:`, value: fmtTradeSummaryNum(s?.balance) },
      { key: "equity", label: `${t("equity")}:`, value: fmtTradeSummaryNum(s?.equity) },
      { key: "margin", label: `${t("margin")}:`, value: fmtTradeSummaryNum(s?.usedMargin) },
    ];
  }, [liveMarginSummary, t]);

  const tradeSummaryRight = useMemo(() => {
    const s = liveMarginSummary;
    const levelBare =
      s?.marginLevel == null || s?.usedMargin <= 0
        ? "—"
        : `${fmtTradeSummaryNum(s.marginLevel, 2)}%`;
    return [
      { key: "free", label: `${t("freeMargin")}:`, value: fmtTradeSummaryNum(s?.freeMargin) },
      { key: "level", label: `${t("level")}:`, value: levelBare },
    ];
  }, [liveMarginSummary, t]);

  const tableTh = embedded
    ? {
        ...thSt(),
        padding: "6px 8px",
        fontSize: 11,
        letterSpacing: 0,
        textTransform: "none",
        fontWeight: 600,
        borderRight: "none",
        boxShadow: "none",
        background: "#ffffff",
        borderBottom: "1px solid #c8c8c8",
        color: "#222222",
      }
    : thSt();
  const tableTd = (tint) =>
    embedded
      ? {
          padding: "6px 8px",
          fontSize: 12,
          lineHeight: 1.4,
          whiteSpace: "nowrap",
          color: EMB.text,
          background: "transparent",
          borderBottom: "none",
          borderRight: "none",
        }
      : tdSt(tint);
  const tdEmbeddedClass = () => (embedded ? "pr-td pr-td--neutral" : undefined);
  const C = darkTheme && !embedded ? C_DARK : C_LIGHT;
  const selEmb = embedded ? selStEmbeddedShell : () => selSt(C);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      className={embedded ? "pr-root pr-root--embedded" : `pr-root fx-book-page${showMobileCards ? " pr-root--mobile-list pr-root--mt5-trade" : ""}`}
      style={{
        padding: embedded ? "0 6px 8px" : showMobileCards ? "0" : "12px 16px",
        background: embedded ? "transparent" : showMobileCards ? "#ffffff" : C.pageBg,
        minHeight: embedded ? 0 : "100%",
        color: embedded ? EMB.text : C.tx,
        fontFamily: "var(--fx-font-sans, Roboto, sans-serif)",
        ...(embedded
          ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 0 }
          : showMobileCards
            ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }
            : {}),
      }}
    >

      {/* ══ FILTER PANEL (hidden on mobile MT5 list) ══ */}
      {!showMobileCards &&
        (!embedded || (!hideFilters && isCloseButtonVisible && closePositonSelected.length > 0)) && (
      <>
      {!embedded && (
        <div className="fx-mobile-report-toolbar">
          <button
            type="button"
            className={`fx-mobile-report-toolbar__btn${mobileFiltersOpen ? " fx-mobile-report-toolbar__btn--active" : ""}`}
            onClick={() => setMobileFiltersOpen((o) => !o)}
          >
            Filters {mobileFiltersOpen ? "▴" : "▾"}
          </button>
          <button type="button" className="fx-mobile-report-toolbar__btn fx-mobile-report-toolbar__btn--primary" onClick={handleGetPosition} disabled={positionReportLoading}>
            {positionReportLoading ? "Loading…" : "Refresh"}
          </button>
        </div>
      )}
      <div
        className={embedded ? "pr-embedded-filters" : `fx-mobile-report-filters${mobileFiltersOpen ? " fx-mobile-report-filters--open" : ""}`}
        style={{
          background: embedded ? EMB.panel : C.panelBg,
          border: embedded ? `1px solid color-mix(in srgb, ${EMB.border} 80%, transparent)` : `1px solid ${C.border}`,
          borderRadius: embedded ? 8 : 5,
          padding: embedded ? "6px 8px" : "14px 16px",
          marginBottom: embedded ? 6 : 4,
          maxHeight: "none",
          overflowY: "visible",
        }}
      >

        {embedded ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "8px 12px",
              justifyContent: "flex-end",
            }}
          >
            {isCloseButtonVisible && closePositonSelected.length > 0 && (
              <button
                type="button"
                onClick={() => setShowConfirmAll(true)}
                disabled={false}
                style={{
                  ...btnStCompact(C.red, false),
                  minWidth: 88,
                }}
              >
                Close ({closePositonSelected.length})
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="fx-book-filter-row" style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start", marginBottom: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {CR_TYPES.map(({ value, label }) => (
                  <label key={value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.tx, userSelect: "none" }}>
                    <input type="radio" name="crtype" value={value} checked={filters.crtype === value} style={{ accentColor: C.headerBg, width: 15, height: 15 }} onChange={() => setF("crtype", value)} />
                    {label}
                  </label>
                ))}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {DISP_TYPES.map(({ value, label }) => (
                  <label key={value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.tx, userSelect: "none" }}>
                    <input type="radio" name="disptype" value={value} checked={filters.disptype === value} style={{ accentColor: C.headerBg, width: 15, height: 15 }} onChange={() => setF("disptype", value)} />
                    {label}
                  </label>
                ))}
              </div>
              <div style={{ minWidth: 200, flex: "1 1 200px", maxWidth: 300 }}>
                <Select styles={selSt(C)} isClearable isLoading={loadingSeg} placeholder={loadingSeg ? "Loading…" : "Segment"} value={segmentSelectValue} onChange={(v) => { setF("segment", v); setF("symbol", null); }} options={segmentOptions} />
              </div>
              <div style={{ minWidth: 160, flex: "1 1 160px", maxWidth: 260 }}>
                <Select styles={selSt(C)} placeholder="Select Valan" value={valanSelectValue} onChange={(v) => setF("valan", v)} options={valanOptions} />
              </div>
              {userRole !== "User" && (
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {REPORT_TYPES.filter((r) => !(userRole === "Partner" && r.value === "script")).map(({ value, label }) => (
                      <label key={value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.tx, userSelect: "none" }}>
                        <input
                          type="radio"
                          name="reporttype"
                          value={value}
                          checked={filters.reporttype === value}
                          style={{ accentColor: C.headerBg, width: 15, height: 15 }}
                          onChange={() => {
                            setF("reporttype", value);
                            setF("clientcode", null);
                            setF("symbol", null);
                            setF("subbroker", null);
                          }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  {filters.reporttype === "client" && (
                    <div style={{ minWidth: 160, flex: "1 1 160px", maxWidth: 260 }}>
                      <Select styles={selSt(C)} isClearable isLoading={loadingCli} placeholder={loadingCli ? "Loading…" : "Select Client"} value={filters.clientcode} onChange={(v) => setF("clientcode", v)} options={clientOptions} />
                    </div>
                  )}
                  {filters.reporttype === "script" && (
                    <div style={{ minWidth: 160, flex: "1 1 160px", maxWidth: 260 }}>
                      <Select styles={selSt(C)} isClearable placeholder="Select Symbol" value={filters.symbol} onChange={(v) => setF("symbol", v)} options={symbolOptions} />
                    </div>
                  )}
                </>
              )}
              {userRole === "User" && (
                <div style={{ minWidth: 160, flex: "1 1 160px", maxWidth: 260 }}>
                  <input value={userName} disabled style={{ ...inpSt(C), opacity: 0.7 }} />
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <button onClick={handleGetPosition} disabled={positionReportLoading} style={btnSt(C.headerBg, positionReportLoading)}>
                {positionReportLoading ? "Loading…" : "Get Position"}
              </button>
              <button onClick={handleExport} disabled={!tableRows.length} style={btnSt(C.headerBg, !tableRows.length)}>
                Export
              </button>
              {isCloseButtonVisible && (
                <button onClick={() => setShowConfirmAll(true)} disabled={closePositonSelected.length === 0} style={{ ...btnSt(C.red, closePositonSelected.length === 0), minWidth: 130 }}>
                  Close Position ({closePositonSelected.length})
                </button>
              )}
            </div>
          </>
        )}
      </div>
      </>
      )}

      {/* ══ TOTALS BAR moved below table (full page) ══ */}

      {/* ══ LOADING BAR (full page — embedded refetch is quiet) ══ */}
      {positionReportLoading && !embedded && (
        <div style={{ height:3, borderRadius:2, marginBottom:4,
          background:`linear-gradient(90deg,${C.headerBg},${C.green})` }} />
      )}

      {/* ══ SEARCH BAR (desktop full page only — not mobile MT5) ══ */}
      {!embedded && !showMobileCards && (
        <div
          className="fx-book-search-row"
          style={{
            background: C.panel2Bg,
            border: `1px solid ${C.border}`,
            borderRadius: 5,
            padding: "8px 16px",
            marginBottom: 4,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <div
            className="fx-book-search-box"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: C.panelBg,
              border: `1px solid ${C.border}`,
              borderRadius: 4,
              padding: "5px 12px",
              flex: "1 1 220px",
              minWidth: 0,
            }}
          >
            <span style={{ color: C.muted, fontSize: 14 }}>🔍</span>
            <input
              placeholder="Search…"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                debouncedSearch(e.target.value);
              }}
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                fontSize: 13,
                color: C.tx,
                fontFamily: "inherit",
              }}
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  debouncedSearch("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: C.muted,
                  fontSize: 11,
                }}
              >
                ✕
              </button>
            )}
          </div>
          <span style={{ marginLeft: "auto", fontSize: 12, color: C.muted }}>
            Total:{" "}
            <strong style={{ color: C.green }}>{totalRecordPositionReport.toLocaleString()}</strong>
            {closePositonSelected.length > 0 && (
              <span style={{ color: C.red, marginLeft: 6 }}>· {closePositonSelected.length} sel</span>
            )}
          </span>
        </div>
      )}

      {/* ══ POSITION LIST (mobile cards) / TABLE (desktop + embedded) ══ */}
      {showMobileCards ? (
        <>
        {/* MT5: account block stays on top; only Positions list scrolls */}
          <div className="pr-mt5-summary pr-mt5-summary--grid" aria-label="Trade account summary">
            <div className="pr-mt5-summary__col">
              {tradeSummaryLeft.map((cell) => (
                <div key={cell.key} className="pr-mt5-summary__cell">
                  <span className="pr-mt5-summary__label">{cell.label}</span>
                  <span
                    className={`pr-mt5-summary__value${cell.bold ? " is-bold" : ""}${
                      cell.tone === "up" ? " is-up" : cell.tone === "down" ? " is-down" : ""
                    }`}
                  >
                    {marginSummaryLoading && !liveMarginSummary ? "···" : cell.value}
                  </span>
                </div>
              ))}
            </div>
            <div className="pr-mt5-summary__col">
              {tradeSummaryRight.map((cell) => (
                <div key={cell.key} className="pr-mt5-summary__cell">
                  <span className="pr-mt5-summary__label">{cell.label}</span>
                  <span className="pr-mt5-summary__value">
                    {marginSummaryLoading && !liveMarginSummary ? "···" : cell.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="pr-mt5-positions-bar">
            <span>{t("positions")}</span>
          </div>

        <div
          className={`pr-mobile-list pr-mobile-list--mt5${
            selectedTradeSheetModel ? " hist-scroll--sheet-open" : ""
          }`}
          onScroll={handleScroll}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {tableRows.length === 0 && !positionReportLoading ? (
            <div className="pr-mobile-list__empty">{t("noOpenPositions")}</div>
          ) : (
            tableRows.map((row, i) => {
              const rid = row.positionId ?? row.PositionId ?? row.rowId;
              return (
                <PositionAdvRow
                  key={rid || i}
                  row={row}
                  selected={
                    selectedTradeSheetId != null &&
                    String(selectedTradeSheetId) === String(rid)
                  }
                  onSelect={onTradeSheetSelect}
                  canClose={isCloseButtonVisible}
                  onClose={(r) => {
                    dispatch(setPositionCloseConfirmDialog(true));
                    dispatch(setPositionCloseData({ ...r, marketType: "market" }));
                  }}
                  orderTypeForRow={orderTypeForRow}
                  C={C}
                />
              );
            })
          )}
        </div>
        {selectedTradeSheetModel
          ? createPortal(
              <TradeMt5DetailSheet
                model={selectedTradeSheetModel}
                onClose={closeTradeSheet}
                onChart={onTradeSheetChart}
                onNewOrder={onTradeSheetNewOrder}
                onModifyClosePosition={
                  isCloseButtonVisible
                    ? (sheetModel) => {
                        const row = sheetModel?.row;
                        if (!row || row.netQty === 0) return;
                        closeTradeSheet();
                        dispatch(setPositionCloseConfirmDialog(true));
                        dispatch(setPositionCloseData({ ...row, marketType: "market" }));
                      }
                    : null
                }
                onClosePosition={
                  isCloseButtonVisible
                    ? (sheetModel) => {
                        const row = sheetModel?.row;
                        if (!row || row.netQty === 0) return;
                        closeTradeSheet();
                        const netLot = Number(row.netLot) || 0;
                        const netQty = Number(row.netQty) || 0;
                        const oneQtySize = netLot ? Math.abs(netQty / netLot) : 1;
                        dispatch(
                          closePosition({
                            ...row,
                            lot: Math.abs(netLot),
                            qty: Math.abs(netQty),
                            price: row.ltp || 0,
                            marketType: "market",
                            oneQtySize,
                            oldNetQty: netQty,
                          })
                        );
                      }
                    : null
                }
              />,
              document.body
            )
          : null}
        </>
      ) : (
      <div
        className={embedded ? "pr-embedded-table-wrap pr-embedded-table-wrap--mt5" : "fx-book-table-scroll"}
        onScroll={embedded ? undefined : handleScroll}
        style={{
          overflowX: embedded ? "hidden" : "auto",
          overflowY: embedded ? "hidden" : "auto",
          maxHeight: embedded ? undefined : "calc(100vh - 370px)",
          flex: embedded ? "1 1 0%" : undefined,
          minHeight: embedded ? 0 : undefined,
          border: embedded ? "1px solid var(--border, #c0b8ae)" : `1px solid ${C.border}`,
          borderRadius: embedded ? 6 : 5,
          background: embedded ? "var(--bg-primary, #fffef9)" : undefined,
          ...(embedded
            ? { display: "flex", flexDirection: "column", gap: 0 }
            : {}),
        }}
      >
        <div
          className={embedded ? "pr-embedded-open-scroll" : undefined}
          onScroll={embedded ? handleScroll : undefined}
          style={
            embedded
              ? { flex: "1 1 0%", minHeight: 0, overflowX: "auto", overflowY: "auto" }
              : undefined
          }
        >
        <table
          className={embedded ? "pr-table pr-table--embedded" : "pr-table pr-table--page"}
          style={{ width: "100%", borderCollapse: "collapse", minWidth: embedded ? 0 : 900 }}
        >
          <thead>
            <tr>
              {/* Checkbox — full page only (MT5 Trade dock has none) */}
              {!embedded && (
              <th
                data-pr-col="select"
                style={{ ...tableTh, width: 44, textAlign: "center", padding: "9px 8px" }}
              >
                {isCheckBoxVisible && tableRows.length > 0 && (
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={(e) => handleCheckAll(e.target.checked)}
                    style={{
                      width: 15,
                      height: 15,
                      cursor: "pointer",
                    }}
                  />
                )}
              </th>
              )}

              {/* Sortable data columns — hover shows MT5-style full title */}
              {[
                ['segment',      'segment',       'segment'],
                ['script',       'symbol',        'symbol'],
                ['openTime',     'time',          'time'],
                ['type',         'type',          'type'],
                ['lots',         'lots',          'lots'],
                ['qty',          'qty',           'qty'],
                ['openPrice',    'price',         'openPriceLabel'],
                ['currentPrice', 'price',         'price'],
                ['profit',       'pnl',           'profit'],
              ].map(([id, key, tipKey]) => (
                <th
                  key={id}
                  data-pr-col={id}
                  data-tip={t(tipKey)}
                  style={tableTh}
                  onClick={() => handleSort(id)}
                >
                  {sortMark(id)}
                  {t(key)}
                </th>
              ))}

              {/* Authority — Admin/Master only */}
              {isAdminRole && (
                <th
                  data-pr-col="authority"
                  data-tip="Authority"
                  style={tableTh}
                  onClick={() => handleSort("authorityName")}
                >
                  {sortMark("authorityName")}
                  Authority
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {tableRows.length === 0 && !positionReportLoading ? (
              embedded && pendingOrders.length > 0 ? null : (
              <tr>
                <td
                  colSpan={prTableColCount}
                  style={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    padding: embedded ? "32px 16px" : 50,
                    minHeight: embedded ? 140 : undefined,
                    color: embedded ? EMB.muted : C.muted,
                    fontSize: embedded ? 12 : 14,
                    background: embedded ? "var(--bg-primary, #fffef9)" : undefined,
                    borderBottom: "none",
                  }}
                >
                  {embedded ? (
                    <>No outstanding positions for these filters. Try another segment, valan, or report filters — the list updates automatically.</>
                  ) : (
                    <>
                      No positions found. Select filters and click <strong style={{ color: C.headerBg }}>Get Position</strong>.
                    </>
                  )}
                </td>
              </tr>
              )
            ) : (
              tableRows.map((row, i) => {
                const mtmVal   = (row.profit ?? row.mtm ?? 0) * (row.currencyRate||1);
                const mtmColor = embedded
                  ? (mtmVal > 0 ? "#1e5eff" : mtmVal < 0 ? "#c62828" : EMB.text)
                  : (mtmVal>0 ? C.green : mtmVal<0 ? C.red : C.tx);
                const tint     = row.netQty>0 ? 'buy' : row.netQty<0 ? 'sell' : 'none';
                const cell     = tableTd(embedded ? "none" : tint);
                const typeIsBuy = String(row.type || orderTypeForRow(row)).toLowerCase().includes("buy");
                const typeClr = embedded
                  ? (typeIsBuy ? "#1e5eff" : "#c62828")
                  : (typeIsBuy ? C.green : C.red);

                return (
                  <tr key={row.rowId||i}
                    className="pr-pos-row"
                    onMouseEnter={e => { if (!embedded) e.currentTarget.style.filter='brightness(0.96)'; }}
                    onMouseLeave={e => { if (!embedded) e.currentTarget.style.filter=''; }}>

                    {!embedded && (
                    <td
                      data-pr-col="select"
                      style={{ ...cell, textAlign:'center', padding:'8px 6px' }}
                      onClick={e => e.stopPropagation()}
                    >
                      {isCheckBoxVisible && row.netQty !== 0 && (
                        <input type="checkbox" checked={!!row.isCheck}
                          onChange={() => handleCheck(row)}
                          style={{ width:15, height:15, cursor:'pointer' }} />
                      )}
                    </td>
                    )}

                    <td className={tdEmbeddedClass()} data-pr-col="segment" style={{ ...cell, fontWeight: embedded ? 400 : 600 }}>{segmentLabel(row)}</td>
                    <td className={tdEmbeddedClass()} data-pr-col="script" style={{ ...cell, fontWeight: embedded ? 400 : 700 }}>{row.script}</td>
                    <td className={tdEmbeddedClass()} data-pr-col="openTime" style={cell}>{formatOpenPositionTime(row.openTime)}</td>
                    <td
                      className={`${tdEmbeddedClass() || ""}${
                        embedded ? (typeIsBuy ? " pr-type--buy" : " pr-type--sell") : ""
                      }`.trim()}
                      data-pr-col="type"
                      style={{
                        ...cell,
                        fontWeight: embedded ? 400 : 700,
                        ...(embedded ? {} : { color: typeClr }),
                      }}
                    >
                      {embedded
                        ? String(row.type || orderTypeForRow(row) || "").toLowerCase()
                        : (row.type || orderTypeForRow(row))}
                    </td>
                    <td className={tdEmbeddedClass()} data-pr-col="lots" style={cell}>{row.lots}</td>
                    <td className={tdEmbeddedClass()} data-pr-col="qty" style={cell}>{row.qty}</td>
                    <td className={tdEmbeddedClass()} data-pr-col="openPrice" style={{ ...cell, fontFamily: FONT_ROBOTO_MONO }}>
                      {Number(row.openPrice||0).toFixed(row.digitAfterPoint||2)}
                    </td>
                    <td className={tdEmbeddedClass()} data-pr-col="currentPrice" style={{ ...cell, fontFamily: FONT_ROBOTO_MONO }}>
                      {Number(row.currentPrice ?? row.ltp ?? 0).toFixed(row.digitAfterPoint||2)}
                    </td>
                    <td
                      className={`${tdEmbeddedClass() || ""}${
                        embedded
                          ? mtmVal > 0
                            ? " pr-pnl--pos"
                            : mtmVal < 0
                              ? " pr-pnl--neg"
                              : ""
                          : ""
                      }`.trim()}
                      data-pr-col="profit"
                      style={{
                        ...cell,
                        fontWeight: embedded ? 400 : 700,
                        ...(embedded ? {} : { color: mtmColor }),
                        position: !isAdminRole ? "relative" : undefined,
                        paddingRight: !isAdminRole && isCloseButtonVisible ? 28 : cell.paddingRight,
                      }}
                    >
                      {mtmVal.toFixed(2)}
                      {(row.currencyRate||1) > 1 && (
                        <span style={{ fontSize:10, marginLeft:4, color:C.muted }}>
                          ({row.currencySymbol}:{row.currencyRate})
                        </span>
                      )}
                      {!isAdminRole && isCloseButtonVisible && row.netQty !== 0 && (
                        <button
                          type="button"
                          className="pr-pos-row__close"
                          title="Close Position"
                          aria-label="Close position"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(setPositionCloseConfirmDialog(true));
                            dispatch(setPositionCloseData({ ...row, marketType: "market" }));
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </td>

                    {/* Authority */}
                    {isAdminRole && (
                      <td
                        className={tdEmbeddedClass()}
                        data-pr-col="authority"
                        style={{
                          ...cell,
                          color: C.muted,
                          position: "relative",
                          paddingRight: isCloseButtonVisible ? 28 : cell.paddingRight,
                        }}
                      >
                        {row.authorityName}
                        {isCloseButtonVisible && row.netQty !== 0 && (
                          <button
                            type="button"
                            className="pr-pos-row__close"
                            title="Close Position"
                            aria-label="Close position"
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(setPositionCloseConfirmDialog(true));
                              dispatch(setPositionCloseData({ ...row, marketType: "market" }));
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}

            {/* ── Rollover — full page only ── */}
            {tableRows.length > 0 && isRolloverVisible && !embedded && (
              <tr>
                <td colSpan={2} style={{ padding:'10px 12px', background:C.panelBg }}>
                  <button
                    onClick={handleRollover}
                    disabled={closePositonSelected.length === 0}
                    style={btnSt(C.headerBg, closePositonSelected.length===0)}>
                    Rollover
                  </button>
                </td>
                <td colSpan={20} style={{ background:C.panelBg }} />
              </tr>
            )}
          </tbody>
        </table>

        {/* Scroll loader */}
        {!embedded && positionReportLoading && tableRows.length > 0 && (
          <div style={{ textAlign:'center', padding:16, color:C.headerBg, fontSize:13 }}>
            Loading more records…
          </div>
        )}

        {/* All loaded */}
        {!embedded && !positionReportLoading && tableRows.length > 0
          && tableRows.length >= totalRecordPositionReport
          && totalRecordPositionReport > 0 && (
          <div style={{ textAlign:'center', padding:10, color:C.muted, fontSize:11 }}>
            ✓ All {totalRecordPositionReport.toLocaleString()} records loaded
          </div>
        )}

        {/* MT5: strip sits directly under open positions (same scroll), then pending */}
        {embedded && (tableRows.length > 0 || pendingOrders.length > 0) && (
          <div className="pr-trade-mt5-strip" role="status" aria-label={t("accountSummary")}>
            <AccountMarginBar
              variant="trade"
              summary={liveMarginSummary}
              loading={marginSummaryLoading}
            />
            {tableRows.length > 0 && totals && (
              <div
                className={
                  "pr-trade-mt5-strip__total" +
                  (totals.tMTM > 0
                    ? " pr-trade-mt5-strip__total--pos"
                    : totals.tMTM < 0
                      ? " pr-trade-mt5-strip__total--neg"
                      : "")
                }
                title="Total P&L"
              >
                {Number(totals.tMTM).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            )}
          </div>
        )}

        {embedded && pendingOrders.length > 0 && (
          <table
            className="pr-table pr-table--embedded pr-table--pending"
            style={{ width: "100%", borderCollapse: "collapse", minWidth: 0 }}
          >
            <tbody className="pr-embedded-pending-body">
              {pendingOrders.map((row, i) => {
                const id = row.id ?? row.Id;
                const side = String(row.orderTypeName || row.OrderTypeName || "").toLowerCase();
                const exec = String(row.executionTypeName || row.ExecutionTypeName || "").toLowerCase();
                const typeLabel = exec || side || "—";
                const isBuy = side.includes("buy") || exec.includes("buy");
                const cell = tableTd("none");
                const lot = row.lot ?? row.Lot ?? "";
                const qty = row.qty ?? row.Qty ?? "";
                const symbol =
                  row.displayName ||
                  row.DisplayName ||
                  row.script ||
                  row.Script ||
                  row.symbol ||
                  row.Symbol ||
                  "—";
                const segment = segmentLabel(row);
                const rate = Number(row.rate ?? row.Rate ?? 0);
                const dp = pendingOrderDigits(row.rate ?? row.Rate);
                const cur = resolvePendingCurrentPrice(row, marketWatchList);
                const setupRaw =
                  row.timeSetupText ||
                  row.TimeSetupText ||
                  row.timeSetup ||
                  row.TimeSetup ||
                  "";
                const setupTime = setupRaw
                  ? formatOpenPositionTime(setupRaw) ||
                    String(setupRaw).replace("T", " ").slice(0, 19)
                  : "—";
                return (
                  <tr key={id || `pend-${i}`} className="pr-pos-row pr-pending-row">
                      <td
                        className={tdEmbeddedClass()}
                        data-pr-col="segment"
                        style={{ ...cell, fontWeight: 400 }}
                      >
                        {segment}
                      </td>
                      <td
                        className={tdEmbeddedClass()}
                        data-pr-col="script"
                        style={{ ...cell, fontWeight: 400 }}
                        title={symbol}
                      >
                        {symbol}
                      </td>
                      <td className={tdEmbeddedClass()} data-pr-col="openTime" style={cell}>
                        {setupTime}
                      </td>
                      <td
                        className={`${tdEmbeddedClass() || ""}${
                          isBuy ? " pr-type--buy" : " pr-type--sell"
                        }`.trim()}
                        data-pr-col="type"
                        style={{
                          ...cell,
                          fontWeight: 400,
                        }}
                      >
                        {String(typeLabel).toLowerCase()}
                      </td>
                      <td className={tdEmbeddedClass()} data-pr-col="lots" style={cell}>
                        {lot !== "" ? lot : "—"}
                      </td>
                      <td className={tdEmbeddedClass()} data-pr-col="qty" style={cell}>
                        {qty !== "" ? qty : "—"}
                      </td>
                      <td
                        className={tdEmbeddedClass()}
                        data-pr-col="openPrice"
                        style={{ ...cell, fontFamily: FONT_ROBOTO_MONO }}
                      >
                        {rate > 0 ? rate.toFixed(dp) : "—"}
                      </td>
                      <td
                        className={tdEmbeddedClass()}
                        data-pr-col="currentPrice"
                        style={{ ...cell, fontFamily: FONT_ROBOTO_MONO }}
                      >
                        {cur > 0 ? Number(cur).toFixed(dp) : "—"}
                      </td>
                      <td
                        className={tdEmbeddedClass()}
                        data-pr-col="profit"
                        style={{
                          ...cell,
                          fontWeight: 400,
                          color: EMB.muted,
                          position: "relative",
                          paddingRight: canCancelPending ? 28 : cell.paddingRight,
                        }}
                      >
                        Placed
                        {canCancelPending && (
                          <button
                            type="button"
                            className="pr-pos-row__close"
                            title="Cancel pending order"
                            aria-label="Cancel pending order"
                            disabled={cancellingPendingId === id}
                            onClick={(e) => {
                              e.stopPropagation();
                              onCancelPendingOrder(id);
                            }}
                          >
                            {cancellingPendingId === id ? "…" : "✕"}
                          </button>
                        )}
                      </td>
                      {isAdminRole && (
                        <td className={tdEmbeddedClass()} data-pr-col="authority" style={cell} />
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
        )}
        </div>
      </div>
      )}

      {/* ══ SINGLE CLOSE POSITION MODAL ══ */}
      {positionCloseConfirmDialog && livePositionCloseRow && livePositionCloseRow.rowId && (
        <ClosePositionModal
          row={livePositionCloseRow}
          loading={isPositionCloseLoading}
          darkTheme={darkTheme}
          mobileMt5={showMobileCards}
          onClose={() => dispatch(setPositionCloseConfirmDialog(false))}
          onConfirm={handleSingleClose}
        />
      )}

      {/* ══ BULK CLOSE CONFIRM MODAL ══ */}
      {showConfirmAll && (
        <ConfirmAllModal
          count={closePositonSelected.length}
          loading={isPositionCloseLoading}
          darkTheme={darkTheme}
          onClose={() => setShowConfirmAll(false)}
          onConfirm={handleCloseAll}
        />
      )}
    </div>
  );
}
